/**
 * Utilidades de sanitización para prevenir XSS y SQL Injection.
 * Implementa defensa en profundidad complementando la sanitización del backend.
 * 
 * @see CancelacionSitioController.java - Backend sanitization
 * @author Equipo Migración GIROP
 * @since Mayo 2026
 */

/**
 * Patrón para detectar posibles ataques de SQL Injection.
 * Detecta: comillas, comentarios SQL, palabras clave peligrosas.
 * 
 * IMPORTANTE: Este patrón es idéntico al del backend para consistencia.
 * @see CancelacionSitioController.SQL_INJECTION_PATTERN
 */
const SQL_INJECTION_PATTERN = /('|--|;|\/\*|\*\/|\b(SELECT|INSERT|UPDATE|DELETE|DROP|UNION|ALTER|EXEC|EXECUTE|TRUNCATE|CREATE|GRANT|REVOKE)\b)/i;

/**
 * Patrón para detectar caracteres HTML/XSS peligrosos.
 */
const XSS_PATTERN = /<[^>]*>|javascript:|on\w+\s*=/i;

/**
 * Resultado de la sanitización.
 */
export interface SanitizacionResult {
  /** true si el texto es válido y fue sanitizado correctamente */
  valido: boolean;
  /** Texto sanitizado (null si entrada vacía, undefined si inválido) */
  texto: string | null | undefined;
  /** Mensaje de error si no es válido */
  error?: string;
}

/**
 * Sanitiza un texto contra XSS y valida contra SQL Injection.
 * 
 * @param texto - Texto a sanitizar
 * @param nombreCampo - Nombre del campo para mensajes de error
 * @param opciones - Opciones de sanitización
 * @returns Resultado con texto sanitizado o error
 * 
 * @example
 * ```typescript
 * const result = sanitizarTexto('NOMBRE VÁLIDO', 'Nombre');
 * if (result.valido) {
 *   console.log(result.texto); // 'NOMBRE VÁLIDO'
 * }
 * 
 * const result2 = sanitizarTexto("'; DROP TABLE--", 'Nombre');
 * if (!result2.valido) {
 *   console.error(result2.error); // "El campo 'Nombre' contiene caracteres no permitidos"
 * }
 * ```
 */
export function sanitizarTexto(
  texto: string | null | undefined,
  nombreCampo: string,
  opciones: {
    /** Si true, convierte a mayúsculas (default: true) */
    aMayusculas?: boolean;
    /** Longitud máxima permitida */
    maxLength?: number;
  } = {}
): SanitizacionResult {
  const { aMayusculas = true, maxLength } = opciones;

  // Texto vacío o null es válido (usar validación required por separado)
  if (texto === null || texto === undefined || texto.trim() === '') {
    return { valido: true, texto: null };
  }

  const textoTrimmed = texto.trim();

  // 1. Validar contra SQL Injection
  if (SQL_INJECTION_PATTERN.test(textoTrimmed)) {
    return {
      valido: false,
      texto: undefined,
      error: `El campo '${nombreCampo}' contiene caracteres no permitidos`
    };
  }

  // 2. Validar contra XSS
  if (XSS_PATTERN.test(textoTrimmed)) {
    return {
      valido: false,
      texto: undefined,
      error: `El campo '${nombreCampo}' contiene caracteres HTML no permitidos`
    };
  }

  // 3. Sanitizar caracteres HTML especiales (escape)
  let textoSanitizado = textoTrimmed
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');

  // 4. Convertir a mayúsculas si se requiere
  if (aMayusculas) {
    textoSanitizado = textoSanitizado.toUpperCase();
  }

  // 5. Truncar si excede longitud máxima
  if (maxLength && textoSanitizado.length > maxLength) {
    textoSanitizado = textoSanitizado.substring(0, maxLength);
  }

  return { valido: true, texto: textoSanitizado };
}

/**
 * Versión simplificada que lanza error si el texto es inválido.
 * Útil cuando se quiere manejar errores con try/catch.
 * 
 * @throws Error si el texto contiene patrones peligrosos
 * 
 * @example
 * ```typescript
 * try {
 *   const nombre = sanitizarTextoStrict(input, 'Nombre', { maxLength: 30 });
 *   // nombre es string | null
 * } catch (error) {
 *   messageService.add({ severity: 'error', detail: error.message });
 * }
 * ```
 */
export function sanitizarTextoStrict(
  texto: string | null | undefined,
  nombreCampo: string,
  opciones: { aMayusculas?: boolean; maxLength?: number } = {}
): string | null {
  const result = sanitizarTexto(texto, nombreCampo, opciones);
  
  if (!result.valido) {
    throw new Error(result.error);
  }
  
  return result.texto ?? null;
}

/**
 * Valida si un texto contiene patrones peligrosos de SQL Injection.
 * Solo valida, no sanitiza.
 */
export function tieneSqlInjection(texto: string | null | undefined): boolean {
  if (!texto) return false;
  return SQL_INJECTION_PATTERN.test(texto);
}

/**
 * Valida si un texto contiene patrones peligrosos de XSS.
 * Solo valida, no sanitiza.
 */
export function tieneXss(texto: string | null | undefined): boolean {
  if (!texto) return false;
  return XSS_PATTERN.test(texto);
}

/**
 * Valida si un texto es seguro (no tiene XSS ni SQL Injection).
 */
export function esTextoSeguro(texto: string | null | undefined): boolean {
  if (!texto) return true;
  return !tieneSqlInjection(texto) && !tieneXss(texto);
}
